<template>

  <div
    class  = "clips-pane-container"
    ref    = "clips_pane"
  >
    <!-- Clips -->
    <div
      v-if    = "hasClips"
      class   = "clips"
    >
      <div class="clip-list">
        <ClipItem
          v-for   = "(clip, index) in clips.clips"
          :key    = "index"
          :index  = "index"
          :clip   = "clip"
        >
        </ClipItem>
      </div>
    </div>

    <Wizard
      v-else
      class   = "wizard"
      icon    = "mdi-playlist-play"
    >
      <span class="text-h5">No clips have been logged.</span>
    </Wizard>

    <!-- Toolbar -->
    <v-toolbar
      dense
      class       = "flex-grow-0"
      elevation   = "0"
      color       = "rgba(0, 0, 0, 0.3)"
    >

      <StatusElement
        label   = "Clips"
        :status = "clips.clips.length.toString()"
      >
      </StatusElement>

      <StatusElement
        class   = "ml-4"
        label   = "Total Duration"
        :status = "totalDuration"
      >
      </StatusElement>

      <v-spacer></v-spacer>

      <ToolbarButton
        tooltip   = "Log Clip"
        :disabled = "!canLog"
        @click    = "onLogClip"
      >
        <v-icon>mdi-playlist-plus</v-icon>
      </ToolbarButton>

      <ToolbarButton
        tooltip   = "Clear All Clips"
        :disabled = "!hasClips"
        @click    = "onClearClips"
      >
        <v-icon>mdi-playlist-remove</v-icon>
      </ToolbarButton>

      <v-divider
        vertical
        class="mx-2"
      >
      </v-divider>

      <v-menu offset-y>
        <template v-slot:activator="{ on: onMenu, attrs }">

          <v-tooltip 
            bottom
            open-delay    = "500"
          >

            <template #activator="{ on: onTooltip }">
              <v-btn
                class     = "px-0"
                height    = "100%"
                min-width = "48px"
                tile
                elevation = "0"
                v-bind    = "attrs"
                v-on      = "{ ...onMenu, ...onTooltip }"
                color     = "transparent"
              >
                <v-icon>mdi-import</v-icon>
              </v-btn>
            </template>

            <span>Import Clip List</span>

          </v-tooltip>

        </template>

        <v-list>
          <v-list-item @click="onOpen">Browse Glim Locations</v-list-item>
          <v-list-item @click="onImport">Browse Your Computer</v-list-item>
        </v-list>
      </v-menu>

      <ToolbarButton
        tooltip   = "Export Clip List"
        :disabled = "!hasClips"
        @click    = "onExport"
      >
        <v-icon>mdi-export</v-icon>
      </ToolbarButton>

      <v-divider
        vertical
        class="mx-2"
      >
      </v-divider>

      <v-btn
        v-if = "selectedOutput && selectedOutput.settings"
        icon
        dark
        @click  = "onShowPluginSettings"
      >
        <v-icon>mdi-cog</v-icon>
      </v-btn>

      <v-select
        class   = "outputs mr-2 elevation-0"
        single-line
        hide-details
        dense
        solo
        v-model   = "selectedOutputName"
        :disabled = "outputs.length === 0"
        :items    = "outputs"
      >
      </v-select>

      <ToolbarButton
        color     = "primary"
        :disabled = "!canSubmit"
        @click    = "onSubmitClips"
      >
        <span class="px-2">Submit</span>
      </ToolbarButton>

    </v-toolbar>

    <input
      ref     = "import_input"
      class   = "hidden"
      type    = "file"
      accept  = ".json"

      @input  = "onFileSelected"
    >

    <ConfirmationDialog ref="confirmation_dialog"></ConfirmationDialog>
    <OutputConfigurationDialog ref="configuration_dialog"></OutputConfigurationDialog>
    <PluginSettingsDialog ref="plugin_settings_dialog"></PluginSettingsDialog>
    <FileBrowser
      ref     = "filebrowser"
      @folder = "lastBrowsedFolder = $event"
      @action = "onFileAction"
    >
    </FileBrowser>
  </div>

</template>

<script>

// #region Imports

/* Vuex */
import { mapState } from 'vuex';

/* Telestream */
import { ToTimeCode, ToSeconds } from '@user-interface/timecode-utilities';
import ClipItem from './components/cliplist/ClipItem';
import FileBrowser from './components/FileBrowser';
import ToolbarButton from './components/buttons/ToolbarButton';
import Wizard from './components/Wizard';
import ConfirmationDialog from './components/dialogs/ConfirmationDialog';
import StatusElement from './components/StatusElement';
import OutputConfigurationDialog from './components/cliplist/OutputConfigurationDialog';
import PluginSettingsDialog from './components/dialogs/PluginSettingsDialog.vue';
import {
  CLEAR_CLIPS,
  COPY_CLIPLIST_PATH,
  EXPORT_CLIPS,
  IMPORT_CLIPS,
  LOAD_CLIPS,
  LOG_CLIP,
  SELECT_CLIPS_OUTPUT,
  SUBMIT_CLIPS,
  UPDATE_PLUGIN_SETTINGS
} from '@/utils/action-types';
import { SET_CLIPS_ELEMENT } from '@/utils/mutation-types';

// #endregion Imports

export default {

  // #region Properties

  name:       'ClipList',
  components: {
    ClipItem,
    ConfirmationDialog,
    FileBrowser,
    OutputConfigurationDialog,
    StatusElement,
    ToolbarButton,
    Wizard,
    PluginSettingsDialog
  },
  data() {
    return {
      lastBrowsedFolder: undefined
    };
  },
  computed: {
    ...mapState([
      'clips',
      'plugins',
      'ui',
      'videoPath'
    ]),
    hasClips() {
      return this.clips.clips.length > 0;
    },
    canLog() {
      return !!this.videoPath;
    },
    canSubmit() {
      return this.hasClips && this.outputs.length > 0 && this.selectedOutputName;
    },
    totalDuration() {
      const durationInSeconds = this.clips.clips
        .map(c => {
          const frames = c.out - c.in;
          if (frames < 0) {
            return 0;
          }
          return ToSeconds(ToTimeCode(frames, c.framerate, c.dropframe), true);
        })
        .reduce(
          ((previous, current) => previous + current),
          0
        );

      return new Date(durationInSeconds * 1000).toISOString().substr(11, 12)
    },
    outputs() {
      return this.clips.outputs.map(s => s.name);
    },
    selectedOutputName: {
      get() {
        return this.clips.selectedOutput;
      },
      set(name) {
        this.$store.dispatch(SELECT_CLIPS_OUTPUT, name);
      }
    },
    selectedOutput() {
      return this.clips.outputs.find(o => o.name === this.selectedOutputName);
    }
  },

  // #endregion Properties

  // #region Life-Cycle

  mounted() {
    this.$store.commit(SET_CLIPS_ELEMENT, this.$refs.clips_pane);
  },

  // #endregion Life-Cycle

  // #region Methods

  methods: {

    // #region Events

    onImport() {
      this.$refs.import_input.click();
    },

    onFileSelected() {
      const file = this.$refs.import_input.files[0];
      if (!file) {
        return;
      }

      this.$store.dispatch(IMPORT_CLIPS, file);
      this.$refs.import_input.value = '';
    },

    async onOpen() {
      const files = await this.$refs.filebrowser.select(
        'Import Clip List',
        this.lastBrowsedFolder,
        [
          {
            command: 'COPY_LINK',
            icon:    'mdi-link',
            tooltip: 'Create Import Link'
          }
        ]
      );
      this.$store.dispatch(LOAD_CLIPS, files);
    },

    onFileAction(action) {
      this.$store.dispatch(COPY_CLIPLIST_PATH, action);
    },

    onExport() {
      this.$store.dispatch(EXPORT_CLIPS);
    },

    onLogClip() {
      this.$store.dispatch(LOG_CLIP);
    },

    async onClearClips() {
      if (await this.$refs.confirmation_dialog.confirm(
        'Clear All Clips?',
        `Are you sure you want remove ${this.clips.clips.length} clip${this.clips.clips.length === 1 ? '' : 's'}? This operation cannot be undone.`,
        'Clear All Clips',
        'error',
        'Keep Clips',
        'primary'
      )) {
        this.$store.dispatch(CLEAR_CLIPS);
      }
    },

    async onSubmitClips() {
      let configuration = {};
      if (this.selectedOutput.schema) {
        configuration = await this.$refs.configuration_dialog.configureOutput(
          this.selectedOutput.name,
          this.selectedOutput.model,
          this.selectedOutput.schema,
          this.selectedOutput.options
        );
      }

      if (configuration) {
        this.$store.dispatch(
          SUBMIT_CLIPS,
          {
            name:          this.selectedOutputName,
            configuration: configuration
          }
        );
      }
    },

    async onGetUpdatedSchema(settingsModel) {
      const newSchemaString = await this.plugins.connection.invoke(
        'GetSchema',
        this.selectedOutput.name,
        JSON.stringify(settingsModel)
      );

      if (newSchemaString == "") // could not get schema from the backend storage
        return;

      return JSON.parse(newSchemaString);
    },

    async onShowPluginSettings() {
      if (this.selectedOutput) {
        const settingsModelString = await this.plugins.connection.invoke('GetSettings', this.selectedOutput.name);

        if (settingsModelString == "") // could not get settings model from the backend storage
          return;

        const schemaString = await this.plugins.connection.invoke('GetSchema', this.selectedOutput.name, settingsModelString);

        if (schemaString == "") // could not get schema from the backend storage
          return;

        const schema = JSON.parse(schemaString);

        const newSettingsModel = await this.$refs.plugin_settings_dialog.configure(
          this.selectedOutput.name,
          JSON.parse(settingsModelString),
          schema.settings.schema,
          schema.settings.options,
          async() => JSON.parse(await this.plugins.connection.invoke('RestoreDefaultSettings', 'ClipListPlugin', this.selectedOutput.name)),
          schema.hasDynamicSettings ? this.onGetUpdatedSchema : undefined
        );

        if (newSettingsModel) {
          this.$store.dispatch(UPDATE_PLUGIN_SETTINGS, { pluginName: this.selectedOutput.name, settingsModel: newSettingsModel });
        }
      }
    }

    // #endregion Events

  }

  // #endregion Methods

}

</script>

<style lang="scss" scoped>

.wizard {
  height:         100%;
}

.clips-pane-container {
  display:        flex;
  flex-direction: column;
  height:         100%;

  .outputs {
    max-width:      200px;
  }

  .clips {
    flex:           1;

    position: relative;
    .clip-list {
      position:       absolute;
      top:            0px;
      right:          0px;
      bottom:         0px;
      left:           0px;
      overflow:       auto;
    }
  }

  .hidden {
    visibility:       hidden;
    height:           0px;
  }
}

</style>
